import { Role } from '../types/auth';
import { OrderState } from '../types/orders';

/**
 * «Convertir actuación» (W4, plan 2026-10-15 § 4): pasar una actuación mal
 * clasificada al otro mundo — básica del recorrido ↔ actuación de una OT —
 * desde el modal de «Transferir». Espejo de `convertAction` del back
 * (`ActionConversionService`), que es quien manda: aquí solo se decide qué
 * se OFRECE.
 */

/** Gesto de oficina (decisión D6): ni Sala, que no crea actuaciones, ni la tablet. */
export const ROLES_CAN_CONVERT_ACTION: readonly Role[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
];

/**
 * Qué actuaciones se convierten (decisión del orquestador, QA T5 15-oct;
 * espejo del back, `ORDERS_CONVERT_ACTION_CLOSED`): las que están en
 * `open`, `partial` o `end` («Finalizada») y NO están validadas
 * (`isValidate`). Una básica de un recorrido entregado suele estar
 * «Finalizada» sin validar.
 */
export const CONVERTIBLE_ACTION_STATES: readonly string[] = [
  OrderState.Open,
  OrderState.Partial,
  OrderState.End,
];

/**
 * `isValidate` llega aparte (no va en la tarjeta): sin saberlo (`undefined`,
 * sin red o sin respuesta) no se ofrece — mejor no ofrecer que un rechazo.
 */
export const canConvertAction = (
  role: Role | string | undefined | null,
  actionState: string | undefined | null,
  isValidate: boolean | undefined | null
): boolean =>
  ROLES_CAN_CONVERT_ACTION.includes(role as Role) &&
  CONVERTIBLE_ACTION_STATES.includes(actionState ?? '') &&
  isValidate === false;

/**
 * Quién transfiere una actuación: espejo del back. El `@Roles` de
 * `transferAction` admite además a Sala, pero `canRoleCreateContentInState` se
 * lo niega siempre (no crea actuaciones), así que aquí no está.
 */
export const ROLES_CAN_TRANSFER_ACTION: readonly Role[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
  Role.Tablet,
];

/**
 * La pastilla «Transferir» de una actuación. Lista de PERMITIDOS (revisión
 * W4, M-10): a quien el back se lo niega no se le pinta — antes a Sala le
 * salía y acababa en un error (análisis 15-oct § 4).
 */
export const canTransferAction = (
  role: Role | string | undefined | null
): boolean => ROLES_CAN_TRANSFER_ACTION.includes(role as Role);

export type ConvertActionMode =
  | 'BASIC_TO_ORDER'
  | 'ORDER_TO_BASIC'
  | 'ANNUL_BASIC_ONLY';

/**
 * El modo de la mutation: una básica va a una OT (copiándose o, si la OT ya
 * tiene el trabajo, solo anulándose); una actuación de OT va a un recorrido.
 */
export const convertModeFor = ({
  sourceIsBasic,
  annulOnly,
}: {
  sourceIsBasic: boolean;
  annulOnly: boolean;
}): ConvertActionMode => {
  if (!sourceIsBasic) return 'ORDER_TO_BASIC';
  return annulOnly ? 'ANNUL_BASIC_ONLY' : 'BASIC_TO_ORDER';
};
